<template lang="pug">
  v-data-table(
    v-model="selected"
    :items="items"
    :headers="tableHeaders"
    :multi-sort="multiSort"
    :sort-by="sortBy"
    :sort-desc="sortDesc"
    :server-items-length="totalItems"
    :options.sync="options"
    :items-per-page="itemsPerPage"
    :no-results-text="noResultsText"
    :hide-default-header="hideDefaultHeader"
    :hide-default-footer="hideDefaultFooter"
    :show-select="showSelect"
    :footer-props="footerProps"
    :no-data-text="$t('No results found')"
    class="m-table"
    :class="{ 'full-height': tableLoading || loading || showEmpty, 'hide-empty': !showEmpty }"
  )
    template(
      v-if="prepend"
      v-slot:body.prepend="{ headers }"
    )
      template(v-if="xsDevice")
        t-table-row-mobile(
          v-for="item in prepend.items"
          :scoped-item="generatePrependScoped(item, prepend.headers)"
          :config="prepend.mobileItemConfig"
          :actions="prepend.itemActions"
        )
      template(v-else)
        t-table-row(
          v-for="item in prepend.items"
          :key="item.id"
          :scopedItem="generatePrependScoped(item, prepend.headers)"
          :actions="prepend.itemActions"
          :config="prepend.itemConfig"
        )
    template(v-slot:top)
      m-table-filters(
        v-if="tableFilters"
        v-bind.sync="filters"
        :items="tableFilters"
      )
      slot(name="tableTop")
    template(v-slot:header.data-table-select="{ on, props }")
      th
        v-simple-checkbox(
          v-bind="props"
          v-on="on"
          color="#8F257D"
          data-test="select-all-checkbox"
        )
    template(
      v-if="tableLoading || loading"
      v-slot:body
    )
      tbody
        td(colspan="6")
          div(class="m-table__loading-wrapper")
            e-progress-circular(
              size="xl"
              width="5"
              color="#8F257D"
            )
    template(v-slot:item="item")
      t-table-row-mobile(
        v-if="xsDevice"
        :scoped-item="item"
        :actions="itemActions"
        :config="mobileItemConfig"
      )
      t-table-row(
        v-else
        :scoped-item="item"
        :actions="itemActions"
        :config="itemConfig"
      )
    template(v-slot:no-data)
      div(
        v-if="skipInitialLoad && !hasFilters"
        class="text-center py-12"
      )
        div(class="text-16px") {{ $t('Please apply at least one filter to load documents') }}
      div(
        v-else-if="showEmpty"
        class="m-table__empty-wrapper"
      )
        m-block-empty-table(
          :title="emptyBlock.title"
          :subtitle="emptyBlock.subtitle"
          :image-name="emptyBlock.image"
          :image-ext="emptyBlock.imageExtension"
        )
          template(v-slot:actions)
            slot(name="emptyTableActions")
</template>

<script>
import EOverlayBlock from '~/components/elements/overlays/e-overlay-block'
import TTableRow from '~/components/templates/t-table-row'
import MBlockEmptyTable from '~/components/modules/blocks/concrete/m-block-empty-table'
import EProgressCircular from '~/components/elements/progress/e-progress-circular'
import TTableRowMobile from '~/components/templates/t-table-row-mobile'
import responsive from '~/mixins/pages/responsive'
import MTableFilters from '~/components/modules/tables/m-table-filters'

export default {
  name: 'MTable',
  components: {
    MTableFilters,
    TTableRowMobile,
    EProgressCircular,
    MBlockEmptyTable,
    EOverlayBlock,
    TTableRow
  },
  mixins: [responsive],
  props: {
    storeName: {
      type: String,
      required: true,
      default: null
    },
    headers: {
      type: Array,
      required: true,
      default: () => []
    },
    itemsPerPage: {
      type: Number,
      default: 50
    },
    noResultsText: {
      type: String,
      default: 'No results found'
    },
    multiSort: {
      type: Boolean,
      default: false
    },
    sortBy: {
      type: [String, Array],
      default: null
    },
    sortDesc: {
      type: Boolean,
      default: false
    },
    search: {
      type: String,
      default: null
    },
    hideDefaultHeader: {
      type: Boolean,
      default: false
    },
    hideDefaultFooter: {
      type: Boolean,
      default: false
    },
    showSelect: {
      type: Boolean,
      default: false
    },
    queryFilter: {
      type: Object,
      default: () => ({})
    },
    prepend: {
      type: Object,
      default: null
    },
    itemActions: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    emptyBlock: {
      type: Object,
      default: () => ({})
    },
    itemConfig: {
      type: Object,
      default: () => ({})
    },
    mobileItemConfig: {
      type: Object,
      default: () => ({})
    },
    customRequest: {
      type: Function,
      default: null
    },
    tableFilters: {
      type: Array,
      default: null
    },
    skipInitialLoad: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tableLoading: false,
      options: {},
      selected: [],
      filters: {}
    }
  },
  computed: {
    // TODO: refactor
    requestParams() {
      // groupBy, groupDesc, itemsPerPage, multiSort, mustSort, page, sortBy, sortDesc
      const { itemsPerPage, page, sortBy, sortDesc } = this.options
      // todo: write map on table params
      const params = { limit: itemsPerPage, offset: page }
      if (sortBy && sortBy.length) {
        params[`order[${sortBy[0]}]`] = sortDesc[0] ? 'desc' : 'asc'
      }
      return params
    },
    items() {
      if (!this.storeName) return []
      return this.$store.state[this.storeName].items
    },
    totalItems() {
      if (this.meta && this.meta.totalItems) {
        return this.meta.totalItems
      } else if (this.items && this.items.length) {
        return this.items.length
      }
      return 0
    },
    meta() {
      if (!this.storeName) return { totalItems: 0 } // todo
      return this.$store.state[this.storeName].meta
    },
    tableHeaders() {
      return this._.map(this.headers, header => ({ ...header, text: this.$t(header.text) }))
    },
    showEmpty() {
      return (
        (!this.items || !this.items.length) &&
        (!this.prepend || !this.prepend.items || !this.prepend.items.length)
      )
    },
    footerProps() {
      return {
        'items-per-page-text': this.$t('Rows per page'),
        'items-per-page-all-text': this.$t('All'),
        'page-text': this.$t('{0}-{1} of {2}'),
        'show-current-page': true,
        'items-per-page-options': [10, 15, 20, 50, 100]
      }
    },
    queryParams() {
      return {
        ...this.queryFilter,
        ...this.transformFilters()
      }
    },
    hasFilters() {
      const transformedFilters = this.transformFilters()
      return Object.keys(transformedFilters).length > 0
    },
    shouldLoadData() {
      if (this.skipInitialLoad) {
        return this.hasFilters
      }
      return true
    },
  },
  watch: {
    options: {
      deep: true,
      immediate: true,
      async handler(options) {
        if (!this._.isEmpty(options) && this.shouldLoadData) {
          await this.fetchData()
        }
      }
    },
    queryParams: {
      deep: true,
      async handler(filters, prev) {
        if (!this._.isEqual(filters, prev)) {
          if (this.skipInitialLoad && !this.hasFilters) {
            this.$store.commit(`${this.storeName}/RESET`)
            return
          }
          await this.fetchData({ offset: 1 })
        }
      }
    },
    search: {
      async handler() {
        if (this.skipInitialLoad && !this.hasFilters && !this.search) {
          return
        }
        await this.fetchData({ 'folder.id': null, offset: 1 })
      }
    },
    selected(value) {
      this.$emit('selected', value)
    },
    filters: {
      deep: true,
      handler(filters) {
        this.$emit('filtersChange', filters)
      }
    }
  },
  created() {
    this.setFiltersFields()
  },
  methods: {
    setFiltersFields() {
      if (Array.isArray(this.tableFilters)) {
        for (const filter of this.tableFilters) {
          this.$set(this.filters, filter.model, null)
        }
      }
    },
    checkFilterValue(filter) {
      if (Array.isArray(this.filters[filter.model])) {
        return !!this.filters[filter.model].length
      } else {
        return !!this.filters[filter.model]
      }
    },
    transformFilters() {
      let processedFilters = {}
      this._.forEach(this.tableFilters, filter => {
        const hasFilterValue = this.checkFilterValue(filter)
        if (hasFilterValue) {
          if (this._.isFunction(filter.transformValue)) {
            const preparedFilter = filter.transformValue(this.filters[filter.model])
            processedFilters = {
              ...processedFilters,
              ...preparedFilter
            }
          } else {
            processedFilters[filter.model] = this.filters[filter.model]
          }
        }
      })
      return processedFilters
    },
    generatePrependScoped(item, headers) {
      return {
        item,
        headers
      }
    },
    async fetchData(params = {}) {
      try {
        this.tableLoading = true
        const requestParams = {
          ...this.requestParams,
          ...this.queryParams,
          ...params,
          search: params.search || this.search || this.queryParams.search
        }
        let response
        if (this._.isFunction(this.customRequest)) {
          response = await this.customRequest(requestParams)
        } else {
          response = await this.$store.dispatch(`${this.storeName}/getAll`, {
            config: {
              params: requestParams
            }
          })
        }
        return response
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.tableLoading = false
      }
    },
    resetSelected() {
      this.selected = []
    }
  }
}
</script>

<style scoped lang="scss">
.m-table {
  height: 100%;

  &::v-deep {
    @media (max-width: map-get($breakpoints, 'sm')) {
      .v-data-footer__select {
        display: none !important;
      }
    }
    .v-data-table-header th.sortable.active {
      color: rgba(0, 0, 0, 0.6) !important;
    }
    .v-data-table-header__icon {
      color: rgba(0, 0, 0, 0.6) !important;
    }
  }

  &.hide-empty {
    &::v-deep {
      .v-data-table__empty-wrapper {
        display: none !important;
      }
    }
  }

  &.full-height {
    &::v-deep {
      .v-data-table__wrapper {
        height: 100%;
      }

      table {
        height: 100%;
      }
    }
  }

  &__loading-wrapper {
    width: 100%;
    height: 100%;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__empty-wrapper {
    width: 100%;
    height: 100%;

    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
